import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute, FaMusic, FaHistory, FaTrash, FaStepBackward, FaStepForward, FaRedo, FaList, FaGripVertical } from 'react-icons/fa';
import ReactDOM from 'react-dom';

// Utility per i cookie
const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; expires=${expires}; path=/`;
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  if (match) {
    try {
      return JSON.parse(decodeURIComponent(match[2]));
    } catch {
      return null;
    }
  }
  return null;
};

// Helper per accedere al player globale (creato da MusicPlayerGlobal)
const getPlayerState = () => window.__ytMusicPlayer || null;
const getPlayer = () => getPlayerState()?.player || null;
const isPlayerReady = () => {
  const state = getPlayerState();
  return state && state.isReady && state.player;
};

// Alias per compatibilità con il codice che usa i nomi "Global"
const getGlobalPlayer = getPlayer;
const getGlobalPlayerState = getPlayerState;
const isGlobalPlayerReady = isPlayerReady;

const MusicPlayer = ({ isChristmasTheme = false }) => {
  // Stato locale del componente
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(() => getCookie('musicPlayerVolume') ?? 30);
  const [isMuted, setIsMuted] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [playerReady, setPlayerReady] = useState(() => isPlayerReady());
  const [buttonPosition, setButtonPosition] = useState({ top: 0, right: 0 });
  const [currentTrack, setCurrentTrack] = useState(() => getCookie('musicPlayerCurrentTrack') ?? 0);
  const [songHistory, setSongHistory] = useState(() => getCookie('musicPlayerHistory') || []);
  const [currentSongTitle, setCurrentSongTitle] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [repeatMode, setRepeatMode] = useState(() => getCookie('musicPlayerRepeat') || 'off');
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  const buttonRef = useRef(null);
  const hasAutoStartedRef = useRef(false);
  const volumeRef = useRef(volume);
  const isMutedRef = useRef(isMuted);
  const isChangingTrackRef = useRef(false);
  const progressIntervalRef = useRef(null);

  // Playlist di default
  const defaultPlaylist = [
    { id: 'sE3uRRFVsmc', title: 'All I Want for Christmas Is You' },
    { id: 'ajPqWxOIjaI', title: 'Last Christmas' }
  ];
  
  // Carica playlist dai cookie o usa quella di default
  const playlist = useRef(() => {
    const savedPlaylist = getCookie('musicPlayerPlaylist');
    if (savedPlaylist && Array.isArray(savedPlaylist) && savedPlaylist.length > 0) {
      return savedPlaylist;
    }
    return defaultPlaylist;
  });
  
  // Inizializza playlist.current chiamando la funzione
  if (typeof playlist.current === 'function') {
    playlist.current = playlist.current();
  }
  
  const currentTrackRef = useRef(getCookie('musicPlayerCurrentTrack') ?? 0);
  const savedPositionRef = useRef(getCookie('musicPlayerPosition') ?? 0);
  const hasResumedRef = useRef(false);

  // Sincronizza lo stato del componente con il player globale al mount
  useEffect(() => {
    const state = getPlayerState();
    
    // Se il player è già pronto e sta suonando, sincronizza lo stato
    if (state?.isReady) {
      setPlayerReady(true);
      setIsPlaying(state.isPlaying || false);
      
      // Aggiorna il titolo corrente
      const trackIndex = state.currentTrackIndex || 0;
      if (playlist.current[trackIndex]) {
        setCurrentSongTitle(playlist.current[trackIndex].title);
        setCurrentTrack(trackIndex);
        currentTrackRef.current = trackIndex;
      }
      
      // Se sta suonando, aggiorna la progress bar e avvia l'interval
      if (state.isPlaying && state.player) {
        setCurrentTime(state.player.getCurrentTime?.() || 0);
        setDuration(state.player.getDuration?.() || 0);
        startProgressInterval();
      }
    }
    
    // Registra listener per gli eventi del player globale
    const handlePlayerEvent = (event, data) => {
      if (event === 'ready') {
        setPlayerReady(true);
        const trackIndex = currentTrackRef.current;
        if (playlist.current[trackIndex]) {
          setCurrentSongTitle(playlist.current[trackIndex].title);
        }
      } else if (event === 'stateChange' && data) {
        if (isChangingTrackRef.current) return;
        
        if (data.data === window.YT?.PlayerState?.ENDED && isPlayerReady()) {
          handleTrackEnd();
        }
        
        if (data.data === window.YT?.PlayerState?.PLAYING) {
          setIsPlaying(true);
          startProgressInterval();
        } else if (data.data === window.YT?.PlayerState?.PAUSED) {
          setIsPlaying(false);
          stopProgressInterval();
        }
      }
    };
    
    if (state?.listeners) {
      state.listeners.add(handlePlayerEvent);
    }
    
    return () => {
      if (state?.listeners) {
        state.listeners.delete(handlePlayerEvent);
      }
      stopProgressInterval();
    };
  }, []); // Solo al mount

  // Salva volume nei cookie e sync ref
  useEffect(() => {
    volumeRef.current = volume;
    setCookie('musicPlayerVolume', volume);
  }, [volume]);

  // Sync muted ref
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Salva repeat mode nei cookie
  useEffect(() => {
    setCookie('musicPlayerRepeat', repeatMode);
  }, [repeatMode]);

  // Salva la traccia corrente nei cookie
  useEffect(() => {
    setCookie('musicPlayerCurrentTrack', currentTrack);
  }, [currentTrack]);

  // Salva periodicamente la posizione di riproduzione
  useEffect(() => {
    if (!isPlaying) return;
    
    const savePositionInterval = setInterval(() => {
      const player = getGlobalPlayer();
      if (player && typeof player.getCurrentTime === 'function') {
        const pos = player.getCurrentTime();
        if (pos > 0) {
          setCookie('musicPlayerPosition', pos);
        }
      }
    }, 2000); // Salva ogni 2 secondi
    
    return () => clearInterval(savePositionInterval);
  }, [isPlaying]);

  // Sync playlist items con playlist ref
  useEffect(() => {
    setPlaylistItems([...playlist.current]);
  }, [currentTrack]);

  // Funzione helper per salvare la playlist nei cookie
  const savePlaylistToCookie = useCallback(() => {
    setCookie('musicPlayerPlaylist', playlist.current);
  }, []);

  // Salva cronologia nei cookie
  useEffect(() => {
    setCookie('musicPlayerHistory', songHistory);
  }, [songHistory]);

  const extractVideoId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Funzione per ottenere il titolo del video da YouTube
  const fetchVideoTitle = async (videoId) => {
    try {
      // Usa oEmbed API di YouTube (non richiede API key)
      const response = await fetch(`https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`);
      const data = await response.json();
      return data.title || `Video ${videoId}`;
    } catch {
      return `Video ${videoId}`;
    }
  };

  // Helper per verificare se il player è realmente pronto
  const checkPlayerReady = useCallback(() => {
    return isGlobalPlayerReady();
  }, []);

  // Formatta tempo in mm:ss
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Aggiorna progress bar
  const startProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    progressIntervalRef.current = setInterval(() => {
      const player = getGlobalPlayer();
      if (player && typeof player.getCurrentTime === 'function') {
        setCurrentTime(player.getCurrentTime() || 0);
        setDuration(player.getDuration?.() || 0);
      }
    }, 500);
  }, []);

  const stopProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Handler per quando una traccia finisce
  const handleTrackEnd = useCallback(() => {
    const currentRepeat = getCookie('musicPlayerRepeat') || 'off';
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    
    if (currentRepeat === 'one') {
      player?.seekTo?.(0);
      player?.playVideo?.();
    } else if (currentRepeat === 'all') {
      isChangingTrackRef.current = true;
      const nextTrack = (currentTrackRef.current + 1) % playlist.current.length;
      currentTrackRef.current = nextTrack;
      state.currentTrackIndex = nextTrack;
      setCurrentTrack(nextTrack);
      setCurrentSongTitle(playlist.current[nextTrack].title);
      player?.loadVideoById?.(playlist.current[nextTrack].id);
      setTimeout(() => { isChangingTrackRef.current = false; }, 500);
    } else {
      if (currentTrackRef.current < playlist.current.length - 1) {
        isChangingTrackRef.current = true;
        const nextTrack = currentTrackRef.current + 1;
        currentTrackRef.current = nextTrack;
        state.currentTrackIndex = nextTrack;
        setCurrentTrack(nextTrack);
        setCurrentSongTitle(playlist.current[nextTrack].title);
        player?.loadVideoById?.(playlist.current[nextTrack].id);
        setTimeout(() => { isChangingTrackRef.current = false; }, 500);
      } else {
        setIsPlaying(false);
      }
    }
  }, []);

  // Traccia il tema precedente per rilevare cambiamenti
  const prevChristmasThemeRef = useRef(isChristmasTheme);
  
  // Quando si attiva il tema Christmas, aggiungi le canzoni di default in cima alla playlist
  useEffect(() => {
    const wasChristmasBefore = prevChristmasThemeRef.current;
    const justActivatedChristmas = isChristmasTheme && !wasChristmasBefore;
    prevChristmasThemeRef.current = isChristmasTheme;
    
    if (justActivatedChristmas && isPlayerReady()) {
      const player = getGlobalPlayer();
      const state = getGlobalPlayerState();
      
      // Aggiungi le canzoni di default in cima (se non ci sono già)
      const currentIds = playlist.current.map(s => s.id);
      const toAdd = defaultPlaylist.filter(s => !currentIds.includes(s.id));
      
      if (toAdd.length > 0) {
        // Inserisci in cima alla playlist
        playlist.current = [...toAdd, ...playlist.current];
        setPlaylistItems([...playlist.current]);
        savePlaylistToCookie();
        state.playlist = playlist.current;
        
        // Vai alla prima canzone di default e fai partire
        currentTrackRef.current = 0;
        state.currentTrackIndex = 0;
        setCurrentTrack(0);
        setCurrentSongTitle(playlist.current[0].title);
        player?.loadVideoById?.(playlist.current[0].id);
        setCookie('musicPlayerStopped', false);
        setCookie('musicPlayerPosition', 0);
      }
    }
  }, [isChristmasTheme, isPlayerReady, savePlaylistToCookie]);

  // Auto-resume: prepara il video alla posizione salvata (l'utente deve cliccare play)
  useEffect(() => {
    if (!playerReady || !isPlayerReady() || hasAutoStartedRef.current) return;
    
    // Segna che abbiamo già tentato
    hasAutoStartedRef.current = true;
    
    const savedPosition = savedPositionRef.current;
    const savedTrack = currentTrackRef.current;
    
    // Solo se c'è una posizione salvata > 0, prepara il video
    if (savedPosition > 0) {
      console.log('[MusicPlayer] Preparing to resume:', { savedTrack, savedPosition });
      
      setTimeout(() => {
        if (!isPlayerReady()) return;
        
        const trackIndex = savedTrack < playlist.current.length ? savedTrack : 0;
        const videoId = playlist.current[trackIndex].id;
        
        // Usa cueVideoById per preparare il video senza avviarlo
        const player = getGlobalPlayer();
        player?.cueVideoById?.({ videoId, startSeconds: savedPosition });
        
        // Mostra la posizione salvata nella progress bar
        setCurrentTime(savedPosition);
        
        // Mostra un messaggio nel titolo per indicare che può riprendere
        const title = playlist.current[trackIndex].title;
        setCurrentSongTitle(`▶ ${title} (${formatTime(savedPosition)})`);
      }, 300);
    }
  }, [playerReady, isPlayerReady]);

  const handleButtonClick = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setShowPanel(!showPanel);
  };

  const togglePlay = () => {
    if (!isPlayerReady()) return;
    const player = getGlobalPlayer();
    if (isPlaying) {
      player?.pauseVideo?.();
      // Salva posizione quando metti in pausa
      const pos = player?.getCurrentTime?.() || 0;
      if (pos > 0) setCookie('musicPlayerPosition', pos);
    } else {
      player?.playVideo?.();
      // Rimuovi il "(riprendi)" dal titolo se presente
      if (currentSongTitle.includes('(riprendi)')) {
        setCurrentSongTitle(playlist.current[currentTrackRef.current].title);
      }
    }
    setIsPlaying(!isPlaying);
  };

  // Previous track
  const playPrevious = () => {
    if (!isPlayerReady() || playlist.current.length <= 1) return;
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    isChangingTrackRef.current = true;
    
    // Se siamo oltre 3 secondi, torna all'inizio della canzone corrente
    if (currentTime > 3) {
      player?.seekTo?.(0);
      isChangingTrackRef.current = false;
      return;
    }
    
    const prevTrack = currentTrackRef.current > 0 
      ? currentTrackRef.current - 1 
      : playlist.current.length - 1;
    
    currentTrackRef.current = prevTrack;
    state.currentTrackIndex = prevTrack;
    setCurrentTrack(prevTrack);
    setCurrentSongTitle(playlist.current[prevTrack].title);
    player?.loadVideoById?.(playlist.current[prevTrack].id);
    
    setTimeout(() => { isChangingTrackRef.current = false; }, 500);
  };

  // Next track
  const playNext = () => {
    if (!isPlayerReady() || playlist.current.length <= 1) return;
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    isChangingTrackRef.current = true;
    
    const nextTrack = (currentTrackRef.current + 1) % playlist.current.length;
    currentTrackRef.current = nextTrack;
    state.currentTrackIndex = nextTrack;
    setCurrentTrack(nextTrack);
    setCurrentSongTitle(playlist.current[nextTrack].title);
    player?.loadVideoById?.(playlist.current[nextTrack].id);
    
    setTimeout(() => { isChangingTrackRef.current = false; }, 500);
  };

  // Seek to position
  const handleSeek = (e) => {
    if (!isPlayerReady() || !duration) return;
    const player = getGlobalPlayer();
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    player?.seekTo?.(seekTime, true);
    setCurrentTime(seekTime);
  };

  // Toggle repeat mode
  const toggleRepeatMode = () => {
    const modes = ['off', 'all', 'one'];
    const currentIndex = modes.indexOf(repeatMode);
    const nextMode = modes[(currentIndex + 1) % modes.length];
    setRepeatMode(nextMode);
  };

  // Get repeat icon style
  const getRepeatStyle = () => {
    if (repeatMode === 'off') return { color: '#9ca3af' };
    if (repeatMode === 'one') return { color: '#10b981', fontWeight: 'bold' };
    return { color: '#10b981' };
  };

  // Drag and drop handlers
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const state = getGlobalPlayerState();
    
    const newItems = [...playlistItems];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    
    // Aggiorna l'indice della traccia corrente se necessario
    if (currentTrackRef.current === draggedIndex) {
      currentTrackRef.current = index;
      state.currentTrackIndex = index;
      setCurrentTrack(index);
    } else if (draggedIndex < currentTrackRef.current && index >= currentTrackRef.current) {
      currentTrackRef.current--;
      state.currentTrackIndex = currentTrackRef.current;
      setCurrentTrack(currentTrackRef.current);
    } else if (draggedIndex > currentTrackRef.current && index <= currentTrackRef.current) {
      currentTrackRef.current++;
      state.currentTrackIndex = currentTrackRef.current;
      setCurrentTrack(currentTrackRef.current);
    }
    
    setDraggedIndex(index);
    setPlaylistItems(newItems);
    playlist.current = newItems;
    state.playlist = newItems;
    savePlaylistToCookie(); // Salva la playlist dopo il drag & drop
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Play specific track from playlist
  const playTrackFromPlaylist = (index) => {
    if (!isPlayerReady() || index === currentTrack) return;
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    isChangingTrackRef.current = true;
    
    currentTrackRef.current = index;
    state.currentTrackIndex = index;
    setCurrentTrack(index);
    setCurrentSongTitle(playlist.current[index].title);
    player?.loadVideoById?.(playlist.current[index].id);
    setCookie('musicPlayerStopped', false);
    
    setTimeout(() => { isChangingTrackRef.current = false; }, 500);
  };

  // Remove track from playlist
  const removeFromPlaylist = (index, e) => {
    e.stopPropagation();
    if (playlist.current.length <= 1) return; // Keep at least one track
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    
    const newPlaylist = [...playlist.current];
    newPlaylist.splice(index, 1);
    playlist.current = newPlaylist;
    setPlaylistItems(newPlaylist);
    savePlaylistToCookie(); // Salva la playlist dopo la rimozione
    state.playlist = newPlaylist;
    
    // Adjust current track index
    if (index < currentTrackRef.current) {
      currentTrackRef.current--;
      state.currentTrackIndex = currentTrackRef.current;
      setCurrentTrack(currentTrackRef.current);
    } else if (index === currentTrackRef.current) {
      // If we removed the current track, play the next one
      const newIndex = Math.min(index, newPlaylist.length - 1);
      currentTrackRef.current = newIndex;
      state.currentTrackIndex = newIndex;
      setCurrentTrack(newIndex);
      setCurrentSongTitle(newPlaylist[newIndex].title);
      if (isPlaying) {
        player?.loadVideoById?.(newPlaylist[newIndex].id);
      }
    }
  };

  const toggleMute = () => {
    if (!isPlayerReady()) return;
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    if (isMuted) {
      player?.unMute?.();
      player?.setVolume?.(volume);
      state.isMuted = false;
      setIsMuted(false);
    } else {
      player?.mute?.();
      state.isMuted = true;
      setIsMuted(true);
    }
  };

  const handleVolume = (e) => {
    const val = parseInt(e.target.value);
    setVolume(val);
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    if (isPlayerReady()) {
      if (val === 0) {
        player?.mute?.();
        state.isMuted = true;
        setIsMuted(true);
      } else {
        // Se era muted e ora volume > 0, unmute
        if (isMuted) {
          player?.unMute?.();
          state.isMuted = false;
          setIsMuted(false);
        }
        player?.setVolume?.(val);
        state.volume = val;
      }
    }
  };

  const changeVideo = async () => {
    if (isLoading) return;
    const newId = extractVideoId(urlInput);
    if (newId && isPlayerReady()) {
      const player = getGlobalPlayer();
      const state = getGlobalPlayerState();
      setIsLoading(true);
      isChangingTrackRef.current = true;
      
      try {
        // Ottieni il titolo
        const title = await fetchVideoTitle(newId);
        
        // Aggiungi alla playlist
        playlist.current.push({ id: newId, title });
        savePlaylistToCookie(); // Salva la playlist dopo l'aggiunta
        state.playlist = playlist.current;
        const newIndex = playlist.current.length - 1;
        currentTrackRef.current = newIndex;
        state.currentTrackIndex = newIndex;
        setCurrentTrack(newIndex);
        setCurrentSongTitle(title);
        
        // Aggiungi alla cronologia (evita duplicati)
        setSongHistory(prev => {
          const filtered = prev.filter(s => s.id !== newId);
          const updated = [{ id: newId, title, addedAt: Date.now() }, ...filtered];
          return updated.slice(0, 20);
        });
        
        player?.loadVideoById?.(newId);
        setCookie('musicPlayerStopped', false);
        setUrlInput('');
      } finally {
        setIsLoading(false);
        setTimeout(() => {
          isChangingTrackRef.current = false;
        }, 500);
      }
    }
  };

  const playFromHistory = (song) => {
    if (!isPlayerReady() || isLoading) return;
    const player = getGlobalPlayer();
    const state = getGlobalPlayerState();
    
    isChangingTrackRef.current = true;
    
    // Aggiungi alla playlist se non c'è già
    const existingIndex = playlist.current.findIndex(s => s.id === song.id);
    if (existingIndex === -1) {
      playlist.current.push(song);
      savePlaylistToCookie(); // Salva la playlist dopo l'aggiunta dalla cronologia
      state.playlist = playlist.current;
    }
    
    const index = existingIndex !== -1 ? existingIndex : playlist.current.length - 1;
    currentTrackRef.current = index;
    state.currentTrackIndex = index;
    setCurrentTrack(index);
    setCurrentSongTitle(song.title);
    player?.loadVideoById?.(song.id);
    setCookie('musicPlayerStopped', false);
    
    setTimeout(() => {
      isChangingTrackRef.current = false;
    }, 500);
  };

  const removeFromHistory = (songId, e) => {
    e.stopPropagation();
    setSongHistory(prev => prev.filter(s => s.id !== songId));
  };

  const clearHistory = () => {
    setSongHistory([]);
  };

  // Resetta la playlist ai valori di default
  const resetPlaylistToDefault = () => {
    if (window.confirm('Vuoi ripristinare la playlist ai brani di default?')) {
      const player = getGlobalPlayer();
      const state = getGlobalPlayerState();
      
      playlist.current = [...defaultPlaylist];
      setPlaylistItems([...defaultPlaylist]);
      currentTrackRef.current = 0;
      state.currentTrackIndex = 0;
      state.playlist = defaultPlaylist;
      setCurrentTrack(0);
      setCurrentSongTitle(defaultPlaylist[0].title);
      savePlaylistToCookie();
      
      if (isPlayerReady()) {
        player?.loadVideoById?.(defaultPlaylist[0].id);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showPanel && buttonRef.current && !buttonRef.current.contains(e.target)) {
        const panel = document.getElementById('music-player-panel');
        if (panel && !panel.contains(e.target)) {
          setShowPanel(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPanel]);

  // Pannello renderizzato con Portal per evitare problemi di layout
  const panelContent = showPanel ? ReactDOM.createPortal(
    <div
      id="music-player-panel"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: buttonPosition.top,
        right: buttonPosition.right,
        width: '320px',
        minWidth: '320px',
        maxWidth: '320px',
        maxHeight: '80vh',
        overflowY: 'auto',
        backgroundColor: '#ffffff',
        border: '2px solid #374151',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: '20px',
        zIndex: 99999,
        boxSizing: 'border-box'
      }}
    >
      {/* Header */}
      <div style={{ 
        textAlign: 'center', 
        fontWeight: 'bold', 
        fontSize: '16px', 
        color: '#1f2937', 
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px'
      }}>
        <span>🎵</span>
        <span>SiSketrontify</span>
      </div>

      {/* Current Song Title */}
      {currentSongTitle && (
        <div style={{
          textAlign: 'center',
          fontSize: '13px',
          color: '#4b5563',
          marginBottom: '16px',
          padding: '8px',
          backgroundColor: '#f0fdf4',
          borderRadius: '8px',
          fontWeight: '500'
        }}>
          🎶 {currentSongTitle}
        </div>
      )}

      {/* Progress Bar */}
      <div style={{ marginBottom: '16px' }}>
        <div 
          onClick={handleSeek}
          style={{
            width: '100%',
            height: '6px',
            backgroundColor: '#e5e7eb',
            borderRadius: '3px',
            cursor: 'pointer',
            position: 'relative'
          }}
        >
          <div style={{
            width: `${duration ? (currentTime / duration) * 100 : 0}%`,
            height: '100%',
            backgroundColor: '#10b981',
            borderRadius: '3px',
            transition: 'width 0.1s'
          }} />
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          color: '#6b7280',
          marginTop: '4px'
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      {/* Playback Controls */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '16px',
        marginBottom: '16px'
      }}>
        {/* Previous */}
        <button
          onClick={playPrevious}
          disabled={!playerReady}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#f3f4f6',
            color: playerReady ? '#374151' : '#9ca3af',
            cursor: playerReady ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FaStepBackward size={14} />
        </button>

        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          disabled={!playerReady}
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: playerReady ? '#10b981' : '#9ca3af',
            color: '#ffffff',
            fontSize: '20px',
            cursor: playerReady ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
          }}
        >
          {isPlaying ? <FaPause /> : <FaPlay style={{ marginLeft: '3px' }} />}
        </button>

        {/* Next */}
        <button
          onClick={playNext}
          disabled={!playerReady}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#f3f4f6',
            color: playerReady ? '#374151' : '#9ca3af',
            cursor: playerReady ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <FaStepForward size={14} />
        </button>
      </div>

      {/* Repeat Mode */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginBottom: '16px' 
      }}>
        <button
          onClick={toggleRepeatMode}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: repeatMode !== 'off' ? '#ecfdf5' : '#f3f4f6',
            border: `1px solid ${repeatMode !== 'off' ? '#10b981' : '#e5e7eb'}`,
            borderRadius: '16px',
            cursor: 'pointer',
            fontSize: '12px',
            ...getRepeatStyle()
          }}
          title={`Repeat: ${repeatMode === 'off' ? 'Off' : repeatMode === 'one' ? 'Singola' : 'Playlist'}`}
        >
          <FaRedo size={12} />
          <span>
            {repeatMode === 'off' ? 'Off' : repeatMode === 'one' ? '1' : 'All'}
          </span>
        </button>
      </div>

      {/* Volume Control */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px', 
        marginBottom: '20px',
        padding: '0 4px'
      }}>
        <button 
          onClick={toggleMute} 
          style={{ 
            background: 'none', 
            border: 'none', 
            cursor: 'pointer', 
            color: '#6b7280',
            padding: '4px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {isMuted ? <FaVolumeMute size={20} /> : <FaVolumeUp size={20} />}
        </button>
        <input
          type="range"
          min="0"
          max="100"
          value={isMuted ? 0 : volume}
          onChange={handleVolume}
          style={{ 
            flex: 1, 
            height: '8px', 
            cursor: 'pointer',
            accentColor: '#10b981'
          }}
        />
        <span style={{ 
          fontSize: '13px', 
          color: '#374151', 
          minWidth: '40px',
          textAlign: 'right',
          fontWeight: '500'
        }}>
          {isMuted ? 0 : volume}%
        </span>
      </div>

      {/* Divider */}
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />

      {/* URL Input */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          placeholder="Incolla URL YouTube..."
          style={{
            width: '100%',
            padding: '10px 14px',
            fontSize: '14px',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            boxSizing: 'border-box',
            outline: 'none'
          }}
          onKeyDown={(e) => e.key === 'Enter' && changeVideo()}
        />
      </div>
      
      <button
        onClick={changeVideo}
        disabled={!urlInput || isLoading}
        style={{
          width: '100%',
          padding: '10px 0',
          fontSize: '14px',
          fontWeight: '500',
          backgroundColor: (urlInput && !isLoading) ? '#3b82f6' : '#d1d5db',
          color: '#ffffff',
          border: 'none',
          borderRadius: '8px',
          cursor: (urlInput && !isLoading) ? 'pointer' : 'not-allowed'
        }}
      >
        {isLoading ? '⏳ Caricamento...' : 'Aggiungi e riproduci'}
      </button>

      {/* Current Playlist Section */}
      <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <button
          onClick={() => setShowPlaylist(!showPlaylist)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#374151',
            fontSize: '13px',
            fontWeight: '600'
          }}
        >
          <FaList size={14} />
          Playlist ({playlistItems.length})
          <span style={{ fontSize: '10px' }}>{showPlaylist ? '▲' : '▼'}</span>
        </button>
        
        {showPlaylist && (
          <button
            onClick={resetPlaylistToDefault}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '11px'
            }}
            title="Ripristina playlist di default"
          >
            <FaRedo size={10} />
            Reset
          </button>
        )}
      </div>

      {showPlaylist && (
        <div style={{ 
          maxHeight: '180px', 
          overflowY: 'auto',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          padding: '8px',
          marginBottom: '16px'
        }}>
          {playlistItems.map((song, index) => (
            <div
              key={`${song.id}-${index}`}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              onClick={() => playTrackFromPlaylist(index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 10px',
                marginBottom: index < playlistItems.length - 1 ? '4px' : 0,
                backgroundColor: index === currentTrack ? '#dcfce7' : '#ffffff',
                borderRadius: '6px',
                cursor: 'grab',
                border: index === currentTrack ? '1px solid #10b981' : '1px solid #e5e7eb',
                opacity: draggedIndex === index ? 0.5 : 1,
                transition: 'all 0.15s'
              }}
            >
              <FaGripVertical size={10} style={{ color: '#9ca3af', marginRight: '8px', flexShrink: 0 }} />
              <span style={{ 
                fontSize: '11px', 
                color: '#6b7280',
                marginRight: '8px',
                minWidth: '18px'
              }}>
                {index + 1}.
              </span>
              {index === currentTrack && isPlaying && (
                <span style={{ marginRight: '6px', fontSize: '10px' }}>▶️</span>
              )}
              <span style={{ 
                fontSize: '12px', 
                color: index === currentTrack ? '#059669' : '#374151',
                fontWeight: index === currentTrack ? '600' : '400',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flex: 1
              }}>
                {song.title}
              </span>
              {playlistItems.length > 1 && (
                <button
                  onClick={(e) => removeFromPlaylist(index, e)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9ca3af',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    flexShrink: 0
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                >
                  <FaTrash size={10} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* History Section */}
      {songHistory.length > 0 && (
        <>
          <div style={{ borderTop: '1px solid #e5e7eb', margin: '16px 0' }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#6b7280',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              <FaHistory size={14} />
              Cronologia ({songHistory.length})
              <span style={{ fontSize: '10px' }}>{showHistory ? '▲' : '▼'}</span>
            </button>
            
            {showHistory && (
              <button
                onClick={clearHistory}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#ef4444',
                  fontSize: '11px'
                }}
              >
                <FaTrash size={10} />
                Svuota
              </button>
            )}
          </div>

          {showHistory && (
            <div style={{ 
              maxHeight: '200px', 
              overflowY: 'auto',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              padding: '8px'
            }}>
              {songHistory.map((song, index) => (
                <div
                  key={song.id}
                  onClick={() => playFromHistory(song)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 10px',
                    marginBottom: index < songHistory.length - 1 ? '4px' : 0,
                    backgroundColor: '#ffffff',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ecfdf5';
                    e.currentTarget.style.borderColor = '#10b981';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#e5e7eb';
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px',
                    flex: 1,
                    minWidth: 0
                  }}>
                    <FaPlay size={10} style={{ color: '#10b981', flexShrink: 0 }} />
                    <span style={{ 
                      fontSize: '12px', 
                      color: '#374151',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {song.title}
                    </span>
                  </div>
                  <button
                    onClick={(e) => removeFromHistory(song.id, e)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#9ca3af',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                  >
                    <FaTrash size={10} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Status */}
      <div style={{ 
        textAlign: 'center', 
        fontSize: '11px', 
        color: '#9ca3af', 
        marginTop: '12px'
      }}>
        {playerReady 
          ? `Traccia ${currentTrack + 1}/${playlistItems.length} • Repeat: ${repeatMode === 'off' ? 'Off' : repeatMode === 'one' ? 'Singola' : 'Playlist'}`
          : '⏳ Caricamento player...'}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleButtonClick}
        style={{
          width: '40px',
          height: '40px',
          minWidth: '40px',
          minHeight: '40px',
          borderRadius: '8px',
          border: isPlaying ? '2px solid #ef4444' : '2px solid #6b7280',
          backgroundColor: isPlaying ? '#ef4444' : '#f9fafb',
          color: isPlaying ? '#ffffff' : '#374151',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          flexShrink: 0
        }}
        title="Music Player"
      >
        <FaMusic size={18} />
      </button>
      {panelContent}
    </>
  );
};

export default MusicPlayer;
